import { supabase } from '@/integrations/supabase/client';

export async function userHasPrimaryShippingAddress(userId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('user_addresses')
      .select('id')
      .eq('user_id', userId as any)
      .eq('address_type', 'shipping' as any)
      .eq('is_primary', true as any)
      .limit(1);
    if (error) throw error;
    return (data?.length || 0) > 0;
  } catch (e) {
    console.error('Error checking primary shipping address:', e);
    return false;
  }
}

/**
 * Syncs profile address data to user_addresses table as a shipping address
 * Creates a new address if no primary shipping address exists, or updates the existing one
 */
interface ShippingPayload {
  first_name?: string;
  last_name?: string;
  phone?: string | null;
  address_line_1: string;
  address_line_2?: string | null;
  city: string;
  state: string;
  postal_code: string;
  country?: string;
}

export async function syncProfileToShippingAddress(
  userId: string,
  payload?: ShippingPayload
): Promise<{ success: boolean; message: string; error?: any }> {
  try {
    let profile = payload;

    if (!profile) {
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('first_name, last_name, phone, address_line_1, address_line_2, city, state, postal_code, country')
        .eq('id', userId)
        .single();

      if (profileError) {
        return { success: false, message: 'Failed to load profile data', error: profileError };
      }

      if (!profileData) {
        return { success: false, message: 'Profile not found' };
      }

      profile = {
        first_name: profileData.first_name || undefined,
        last_name: profileData.last_name || undefined,
        phone: profileData.phone || null,
        address_line_1: profileData.address_line_1,
        address_line_2: profileData.address_line_2 || undefined,
        city: profileData.city,
        state: profileData.state,
        postal_code: profileData.postal_code,
        country: profileData.country || undefined,
      } as ShippingPayload;
    }

    if (!profile) {
      return { success: false, message: 'No profile data available' };
    }

    if (!profile.address_line_1 || !profile.city || !profile.state || !profile.postal_code) {
      return { 
        success: false, 
        message: 'Please complete your address information in your profile before syncing (address, city, state, and postal code are required)' 
      };
    }

    // Check if user already has a primary shipping address
    const { data: existingAddress, error: checkError } = await supabase
      .from('user_addresses')
      .select('id')
      .eq('user_id', userId)
      .eq('address_type', 'shipping')
      .eq('is_primary', true)
      .limit(1)
      .single();

    const addressData = {
      user_id: userId,
      address_type: 'shipping' as const,
      label: 'Primary Shipping (from Profile)',
      first_name: profile.first_name || '',
      last_name: profile.last_name || '',
      address_line_1: profile.address_line_1,
      address_line_2: profile.address_line_2 || null,
      city: profile.city,
      state_province: profile.state,
      postal_code: profile.postal_code,
      country: profile.country || 'United States',
      phone: profile.phone || null,
      is_primary: true,
    };

    if (existingAddress && !checkError) {
      // Update existing primary shipping address
      const { error: updateError } = await supabase
        .from('user_addresses')
        .update(addressData)
        .eq('id', existingAddress.id);

      if (updateError) {
        return { success: false, message: 'Failed to update shipping address', error: updateError };
      }

      return { success: true, message: 'Primary shipping address updated from your profile!' };
    } else {
      // Create new primary shipping address
      const { error: insertError } = await supabase
        .from('user_addresses')
        .insert(addressData);

      if (insertError) {
        return { success: false, message: 'Failed to create shipping address', error: insertError };
      }

      return { success: true, message: 'Primary shipping address created from your profile!' };
    }
  } catch (e) {
    console.error('Error syncing profile to shipping address:', e);
    return { success: false, message: 'An unexpected error occurred', error: e };
  }
}

export async function getPrimaryShippingAddress(userId: string) {
  try {
    const { data, error } = await supabase
      .from('user_addresses')
      .select('*')
      .eq('user_id', userId as any)
      .eq('address_type', 'shipping' as any)
      .eq('is_primary', true as any)
      .limit(1);
    if (error) throw error;
    return data?.[0] || null;
  } catch (e) {
    console.error('Error fetching primary shipping address:', e);
    return null;
  }
}

export async function getUserMissingInfo(userId: string): Promise<{
  missingPaymentMethod: boolean;
  missingShippingAddress: boolean;
  missingPaddleNumber: boolean;
}> {
  try {
    // Payment method
    const { data: pm, error: pmErr } = await supabase
      .from('user_payment_methods')
      .select('id')
      .eq('user_id', userId as any)
      .eq('is_verified', true as any)
      .limit(1);
    if (pmErr) throw pmErr;
    const missingPaymentMethod = !(pm && pm.length > 0);

    // Shipping address
    const hasAddress = await userHasPrimaryShippingAddress(userId);
    const missingShippingAddress = !hasAddress;

    // Paddle
    const { data: prof, error: profErr } = await supabase
      .from('profiles')
      .select('paddle_number')
      .eq('id', userId as any)
      .single();
    if (profErr) throw profErr;
    const missingPaddleNumber = !(prof && (prof as any).paddle_number);

    return { missingPaymentMethod, missingShippingAddress, missingPaddleNumber };
  } catch (e) {
    console.error('Error computing missing user info:', e);
    return { missingPaymentMethod: true, missingShippingAddress: true, missingPaddleNumber: false };
  }
}


